/**
 * -----------------------------------------------------------------------------
 * Vehicle Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single vehicle of the organization.
 */

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { FormikProps, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import * as Yup from "yup";
import {
  useEditOrganizationVehicleImageMutation,
  useEditOrganizationVehicleMutation,
  useLazyOrganizationVehiclesQuery,
  useOrganizationVehiclesQuery,
  useSingleOrganizationDriverQuery,
  useSingleOrganizationVehicleQuery,
  useValidateUniqueIdMutation,
} from "../../../api/network/adminApiServices";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  setListingQueryParams,
  TListingQueryParams,
  TModalsState,
} from "../../../api/store/commonSlice";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import {
  dateToGregorianDays,
  dbTimeToJsTime,
  gregorianDaysToJsDate,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import {
  convertKmphTo,
  convertSpeedToKmph,
  getDistanceByUnit,
  getDistanceByUnitUnrounded,
  getDistanceInKms,
} from "../../../utils/distance";
import { driverDisplayNameForAssetAdmin } from "../../../utils/driver";
import { convertFuelEconomy, convertToKmlForApi } from "../../../utils/economy";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { consistentDisplayAssetItem } from "../../../utils/vehicle";
import { convertLitersTo, convertToLitersForApi } from "../../../utils/volume";
import useVisionMax from "../../camera-admin/useVisionMax";
import {
  getVehicleFieldValueFromUUID,
  getVehicleUUID,
  mapVehicleDisplayTitle,
} from "../../map-fleet/common";
import {
  DISTANCE_UNIT_VALUES,
  ECONOMY_UNITS,
  ECONOMY_UNITS_OPTIONS,
  ECONOMY_UNITS_VALUES,
  SPEED_UNIT_VALUES,
  SPEED_UNITS,
  SPEED_UNITS_OPTIONS,
  VOLUME_UNIT_VALUES,
  VOLUME_UNITS,
  VOLUME_UNITS_OPTIONS,
} from "../account/constants";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import ModalAssetDecodeVIN from "./components/modalAssetDecodeVIN";
import { CAMERA_TYPES, IGNITION_INPUT_OPTIONS } from "./constants";
import ModalResetConfirmation from "./modalResetConfirmation";
import {
  TFormFieldNames,
  vehicleFormInitialValues,
  vehicleUploadFormInitialValues,
  vehicleUploadFormValidationSchema,
} from "./validation";
import {
  VehicleCameraIdDetailForm,
  VehicleDetailForm,
  VehicleGroupMembershipForm,
  VehicleZipTieForm,
  VehicleZipTieFormHandle,
} from "./vehicle-form";
import {
  VehicleDynamicInputLabels,
  VehicleDynamicInputLabelsHandle,
} from "./components/dynamicInputLabels";

const ScreenAdminDetailVehicle = () => {
  const { vehicleId: vehicleUUID } = useParams<{ vehicleId: any }>();
  const vehicleId = getVehicleFieldValueFromUUID(vehicleUUID, "deviceid");
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.vehicles",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.vehicles.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useVisionMax();

  const { getUserACLAccessLevel } = useACL();
  const thisOrgZipTieAllowed = useLoggedInUserData("serializedziptieallowed");
  const thisOrgPtoLabelsAllowed = useLoggedInUserData("allowptolabels");
  const { getLocalStorageItem } = useLocalStorage();

  const enterpriseAccountData = getLocalStorageItem(
    localStorageKeys.ownerOrganization
  );

  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { vehicles: orgVehiclesQueryParams } = listingQueryParams;

  // flag to idenfiy if user is coming from create new vehicle popup
  const isNewEntity = useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );
  const [decodeVinData, setDecodeVinData] = useState({
    make: "",
    model: "",
    modelyear: "",
  });

  // prepare query params for fetching organization vehicles
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        vehicles: { ...orgVehiclesQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization vehicles
  const {
    data: dataOrgVehicles,
    isFetching: isFetchingOrgVehicles,
    error,
  } = useOrganizationVehiclesQuery({
    ...orgVehiclesQueryParams,
    // 'search_fields' intentionally not added here as default search fields are defined in the API service
  });
  const { results, count } = dataOrgVehicles || {};

  // fetch single vehicle details
  const {
    data: dataSingleVehicle,
    isFetching: isFetchingSingleVehicle,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationVehicleQuery(
    {
      account_id: thisUserOrganizationId,
      device_id: vehicleId,
    },
    {
      skip: !vehicleId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );

  const zipTieFormRef = useRef<VehicleZipTieFormHandle>(null);
  const dynamicInputLabelsRef = useRef<VehicleDynamicInputLabelsHandle>(null);
  const [dynamicInputLabelsAreSaving, setDynamicInputLabelsAreSaving] =
    useState(false);

  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
      ) {
        toast.error(t("toast.vehicle_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.vehicles);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  const { data: dataSingleDriver, isFetching: isFetchingSingleDriver } =
    useSingleOrganizationDriverQuery(
      {
        account_id: thisUserOrganizationId,
        driver_id: `${dataSingleVehicle?.driverid}`,
      },
      { skip: !dataSingleVehicle?.driverid }
    );

  // vehicle mutations
  const [
    editOrganizationVehicleApiTrigger,
    { isLoading: isLoadingEditVehicle },
  ] = useEditOrganizationVehicleMutation();
  // const [deleteSingleVehicleApiTrigger, { isLoading: isLoadingDeleteVehicle }] =
  //   useDeleteSingleVehicleMutation();
  const [
    editOrganizationVehicleImageAPITrigger,
    { isLoading: isLoadingEditVehicleImage },
  ] = useEditOrganizationVehicleImageMutation();

  // unique id validation
  const [validateUniqueIdTrigger] = useValidateUniqueIdMutation();

  // video allowed at account level
  const thisOrgViewVideoAllowed = useLoggedInUserData("viewVideoAllowed");

  // speed units
  const orgSpeedUnit = useLoggedInUserData("speedUnits") as SPEED_UNIT_VALUES;
  const speedUnit: SPEED_UNITS =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnit)?.unit ||
    SPEED_UNITS_OPTIONS[0].unit;

  // distance unit
  const orgDistanceUnit = useLoggedInUserData(
    "distanceUnits"
  ) as DISTANCE_UNIT_VALUES;

  // volume units
  const orgVolumeUnit = useLoggedInUserData(
    "volumeUnits"
  ) as VOLUME_UNIT_VALUES;
  const volumeUnit: VOLUME_UNITS =
    VOLUME_UNITS_OPTIONS.find((option) => option.value === orgVolumeUnit)
      ?.unit || VOLUME_UNITS_OPTIONS[0].unit;

  // economy units
  const orgEconomyUnit = useLoggedInUserData(
    "economyUnits"
  ) as ECONOMY_UNITS_VALUES;
  const economyUnit: ECONOMY_UNITS =
    ECONOMY_UNITS_OPTIONS.find((option) => option.value === orgEconomyUnit)
      ?.unit || ECONOMY_UNITS_OPTIONS[0].unit;

  const [
    getLazyOrgVehiclesAPITrigger,
    { isFetching: isLazyFetchingOrgVehicles },
  ] = useLazyOrganizationVehiclesQuery();

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingSingleVehicle);
  }, [isFetchingSingleVehicle]);

  const formik: FormikProps<typeof vehicleFormInitialValues> = useFormik<
    typeof vehicleFormInitialValues
  >({
    initialValues: vehicleFormInitialValues,
    validationSchema: Yup.object().shape({
      // static value checks
      // disabled asset model validation as this is not required
      // vehicle_model: Yup.string().required("Asset Model is required"), // number().max(currentYear, "Asset model cannot be in the future"),
      vehicle_description: Yup.string().required(
        "Asset Description is required"
      ),
      is_active: Yup.boolean().required("Is Active is required"),
      creation_date: Yup.date().required("Creation Date is required"),
      short_name: Yup.string().required("Display Name is required"),
      ignition_input: Yup.string().required("Ignition Input is required"),
      // phone_number: Yup.string().matches(phoneRegex, "Invalid phone"),
      // driver_id: Yup.string()
      //   .required("Please select a driver"),
      recorder_type: Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required("Recorder Type is required"),
      recorder_id: Yup.string().when(
        ["recorder_type"],
        ([recorder_type], schema) => {
          // if recorder_type is set to none, Camera ID can be optional and kept disabled
          if (recorder_type?.toString() === CAMERA_TYPES.NONE.toString()) {
            return schema.optional();
          }
          // otherwise Camera ID is required
          return schema
            .test(
              "is-non-empty",
              "Please enter a Camera ID",
              (value) => value !== ""
            )
            .required("Camera ID is required");
        }
      ),
      maximum_speed: Yup.number()
        .typeError("Please enter a numeric value")
        .test(
          "max-2-decimals",
          "Maximum Speed can have at most 2 decimal places",
          (value) => {
            if (value === undefined || value === null || (value as any) === "")
              return true;
            // Check if value is integer or has at most 2 decimal places
            return /^\d+(\.\d{1,2})?$/.test(value.toString());
          }
        ),
      selected_groups: Yup.array()
        .of(Yup.object())
        .when("all_groups", {
          is: false,
          then: (schema) =>
            schema.min(
              1,
              'Either select "All Groups" option above, or at least one group from the option below'
            ),
          otherwise: (schema) => schema,
        }),
      odometer_offset: Yup.number()
        .typeError("Please enter a numeric value")
        .nullable(),
      engine_hours_offset: Yup.number()
        .typeError("Please enter a numeric value")
        .nullable(),

      // dynamic value checks
      unique_id: Yup.string()
        .optional()
        .test(
          "Unique ID",
          "The entered Unique ID is already assigned to another asset. Please enter a different Unique ID.",
          (value) => {
            if (!value) return true;
            return new Promise((resolve) => {
              validateUniqueIdTrigger({
                // page: 1,
                // page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
                // uniqueid: value,
                unique_id: value,
                device_id: vehicleId,
              })
                .unwrap()
                .then((response) => {
                  if (response?.is_unique === false) {
                    resolve(false);
                  } else {
                    resolve(true);
                  }
                })
                .catch(() => {
                  resolve(true);
                });
            });
          }
        ),

      fuel_capacity: Yup.number()
        .typeError("Please enter a numeric value")
        .test(
          "max-2-decimals",
          "Fuel Capacity can have at most 2 decimal places",
          (value) => {
            if (value === undefined || value === null || (value as any) === "")
              return true;
            // Check if value is integer or has at most 2 decimal places
            return /^\d+(\.\d{1,2})?$/.test(value.toString());
          }
        ),

      fuel_economy: Yup.number()
        .typeError("Please enter a numeric value")
        .test(
          "max-2-decimals",
          "Fuel Economy can have at most 2 decimal places",
          (value) => {
            if (value === undefined || value === null || (value as any) === "")
              return true;
            // Check if value is integer or has at most 2 decimal places
            return /^\d+(\.\d{1,2})?$/.test(value.toString());
          }
        ),

      fuel_cost: Yup.number()
        .typeError("Please enter a numeric value")
        .test(
          "max-2-decimals",
          "Fuel Cost can have at most 2 decimal places",
          (value) => {
            if (value === undefined || value === null || (value as any) === "")
              return true;
            // Check if value is integer or has at most 2 decimal places
            return /^\d+(\.\d{1,2})?$/.test(value.toString());
          }
        ),
    }),
    onSubmit: (values) => {
      // prepare payload
      const preparedVehiclePayload: Partial<TLegacyVehicle> = {
        // TODO: [P] need to change payload type
        accountid: `${listingQueryParams.vehicles.accountid}`,
        assettype: values.asset_type,
        //codeversion: values.firmware_version, This value should not be changed from frontend
        description: values.vehicle_description,
        deviceid: values.vehicle_id,
        displayname: values.short_name,
        driverid: values.driver_id,
        ...(getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EQUIP_STATUS) >=
        ROLE_ACCESS_LEVELS.WRITE
          ? {
              equipmentstatus: values.equipment_status,
            }
          : {}),
        // equipmenttype: values.equipment_type, // equipmenttype is ECM VIN modified by device automatically - we should not update this
        ...(getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FUEL_CAPACITY) >=
        ROLE_ACCESS_LEVELS.WRITE
          ? {
              fuelcapacity: convertToLitersForApi(
                volumeUnit,
                values.fuel_capacity
              ),
            }
          : {}),
        fuelcostperliter: values.fuel_cost,
        ...(getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_FUEL_ECONOMY) >=
        ROLE_ACCESS_LEVELS.WRITE
          ? {
              fueleconomy: convertToKmlForApi(
                economyUnit?.toString() as ECONOMY_UNITS,
                values.fuel_economy
              ),
            }
          : {}),
        fueltype: values.fuel_type,
        // group_ids: values.selected_groups.map((item)=>item?.value).join(",") || "", // comma separated groupids
        group_ids:
          values.selected_groups
            .map((item) =>
              typeof item === "object" && item !== null ? item?.value : item
            )
            .join(",") || "",
        ignitionindex: values.ignition_input,
        // imeinumber: values.imei_number,
        devicecode: values.server_id, // ipaddresscurrent: values.server_id,
        ...(getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_ACTIVE) >=
        ROLE_ACCESS_LEVELS.WRITE
          ? {
              isactive: values.is_active ? 1 : 0,
            }
          : {}),
        licenseexpire: dateToGregorianDays(new Date(values.license_expiration)),
        licenseplate: values.license_plate,
        // previousrecorderid: values.previous_recorder_id, // this field should be updated at backend
        // pushpinid: values.group_pushpin_id,
        ...(thisOrgViewVideoAllowed === "1" &&
        getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EDIT_CAMERA) >=
          ROLE_ACCESS_LEVELS.WRITE
          ? {
              cameraid:
                values.recorder_type.toString() === CAMERA_TYPES.NONE.toString()
                  ? ""
                  : values.recorder_id,
            }
          : {}),
        ...(thisOrgViewVideoAllowed === "1" &&
        getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EDIT_CAMERA) >=
          ROLE_ACCESS_LEVELS.WRITE
          ? {
              recorderidlastchange: jsTimeToDBTime(
                values.recorder_id_last_changed
              ),
            }
          : {}),
        ...(thisOrgViewVideoAllowed === "1" &&
        getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EDIT_CAMERA) >=
          ROLE_ACCESS_LEVELS.WRITE
          ? {
              cameraonoff: values.recorder_on ? 1 : 0,
            }
          : {}),
        ...(thisOrgViewVideoAllowed === "1" &&
        getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EDIT_CAMERA) >=
          ROLE_ACCESS_LEVELS.WRITE
          ? {
              recordertype: values.recorder_type,
            }
          : {}),
        // routesegcolorsel: values.map_route_color,
        // serialnumber: values.serial_number,
        // simphonenumber: values.phone_number,
        // smsemail: values.email,
        speedlimitkph: !!values.maximum_speed
          ? convertSpeedToKmph(speedUnit, values.maximum_speed)
          : 0,
        ...(getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_UNIQUE_ID) >=
        ROLE_ACCESS_LEVELS.WRITE
          ? {
              uniqueid: values.unique_id,
            }
          : {}),
        vehicleclass: values.vehicle_class,
        vehicleid: values.equipment_type, // vehicleid is VIN that is updated manually //values.vin
        equipmenttype: values.vin,
        vehiclemake: values.vehicle_make,
        vehiclemodel: values.vehicle_model,
        vehicleyear: values.vehicle_year || null,
        odometeroffsetkm: getDistanceInKms(
          Number(values?.odometer_offset),
          orgDistanceUnit.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        lastodometerkm: getDistanceInKms(
          Number(values?.reported_odometer),
          orgDistanceUnit.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        enginehoursoffset: Number(values.engine_hours_offset),
        lastenginehours: Number(values.reported_hours),
        bordercrossing: values.is_border_crossing ? 1 : 0,
        sparseeventreporting: values.sparseeventreporting ? 1 : 0,
      };

      const iconSettingsFormData = new FormData();
      iconSettingsFormData.append("deviceid", vehicleId);
      iconSettingsFormData.append("display_mode", values.icon_type || "");
      iconSettingsFormData.append(
        "border_color_code",
        values.map_border_color || ""
      );
      iconSettingsFormData.append(
        "standard_icon_identifier",
        values.standard_icon_key || ""
      );
      if (uploadFormik.values.image) {
        iconSettingsFormData.append("image", uploadFormik.values.image as File);
      }

      if (
        dataSingleVehicle?.isactive == 1 &&
        preparedVehiclePayload.isactive == 0
      ) {
        // Copy uniqueID to previousUniqueID column and set uniqueIDLastChange to current timestamp.
        preparedVehiclePayload.previousuniqueid =
          preparedVehiclePayload.uniqueid;
        preparedVehiclePayload.uniqueidlastchange = Math.floor(
          Date.now() / 1000
        );
        // Copy cameraID to previousRecorderID column and set recorderIDLastChange to current timestamp.
        preparedVehiclePayload.previousrecorderid =
          preparedVehiclePayload.cameraid ?? dataSingleVehicle?.cameraid ?? "";
        preparedVehiclePayload.recorderidlastchange = Math.floor(
          Date.now() / 1000
        );

        // Clear out uniqueID and cameraID fields.
        preparedVehiclePayload.uniqueid = "";
        preparedVehiclePayload.cameraid = "";
      }

      if (zipTieFormRef.current && !zipTieFormRef.current?.validateChanges()) {
        return;
      }

      if (
        dynamicInputLabelsRef.current &&
        !dynamicInputLabelsRef.current.validateChanges()
      ) {
        return;
      }

      // call api for updating vehicle
      editOrganizationVehicleApiTrigger(preparedVehiclePayload)
        .unwrap()
        .then(() => {
          return editOrganizationVehicleImageAPITrigger(
            iconSettingsFormData
          ).unwrap();
        })
        .then(() => {
          return zipTieFormRef.current?.saveChanges();
        })
        .then(() => {
          return dynamicInputLabelsRef.current?.saveChanges();
        })
        .then(() => {
          toast.success(t("toast.vehicle_updated"));
          navigate(routeUrls.dashboardChildren.adminChildren.vehicles);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });
  const uploadFormik = useFormik({
    initialValues: {
      ...vehicleUploadFormInitialValues,
      deviceid: vehicleId,
    },
    validationSchema: vehicleUploadFormValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // intentionally disabled returning because this will allow
      // the removal of image that was previously set
      // --------------------------------------------------------
      // if (!values.image) {
      //   return;
      // }
      const formData = new FormData();
      formData.append("deviceid", values.deviceid);
      if (values.image) {
        formData.append("image", values?.image as File);
      }
      formData.append("display_mode", formik.values.icon_type || "");
      formData.append(
        "border_color_code",
        formik.values.map_border_color || ""
      );
      formData.append(
        "standard_icon_identifier",
        formik.values.standard_icon_key || ""
      );
      editOrganizationVehicleImageAPITrigger(formData)
        .unwrap()
        .then((data) => {
          if (APP_CONFIG.DEBUG.ALL) console.log("RES DATA", data);
          if (!!data?.message) {
            toast.success(t("toast.vehicle_updated"), { autoClose: 10000 });
          } else {
            toast.error(t("toast.updation_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleVehicle) {
      if (!!dataSingleDriver?.driverid && isFetchingSingleDriver) return;
      // Don't overwrite the form while the user is actively editing — this can happen
      // when a background mutation (e.g. VIN decode) invalidates VEHICLE_SINGLE and
      // triggers a refetch, which would reset all unsaved field values and exit edit mode.
      if (userCanEdit && !isNewEntity?.current) return;
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        vehicle_id: dataSingleVehicle?.deviceid,
        asset_image_url: dataSingleVehicle?.assetimageurl || "",
        icon_type: dataSingleVehicle?.display_mode || "",
        standard_icon_key: dataSingleVehicle?.standard_icon_identifier || "",
        map_border_color: dataSingleVehicle?.border_color_code || "#ffffff00",
        creation_date: new Date(
          dbTimeToJsTime(dataSingleVehicle?.creationtime)
        ),
        server_id: dataSingleVehicle?.devicecode || "", // dataSingleVehicle?.ipaddresscurrent || "",
        firmware_version: dataSingleVehicle?.codeversion || "", // TODO: [P] need to check proper keys
        previous_unique_id: dataSingleVehicle?.previousuniqueid || "",
        unique_id_last_changed: dbTimeToJsTime(
          dataSingleVehicle?.uniqueidlastchange
        ),
        unique_id: dataSingleVehicle?.uniqueid || "",
        groupid: dataSingleVehicle?.groupid ?? "",
        reported_odometer: getDistanceByUnit(
          dataSingleVehicle?.lastodometerkm || 0,
          orgDistanceUnit.toString() as DISTANCE_UNIT_VALUES
        ) as number,
        reported_hours: dataSingleVehicle?.lastenginehours || 0,
        odometer_offset: String(
          getDistanceByUnitUnrounded(
            dataSingleVehicle?.odometeroffsetkm || 0,
            orgDistanceUnit.toString() as DISTANCE_UNIT_VALUES
          )
        ),

        odometer_sum: String(
          (
            (getDistanceByUnit(
              dataSingleVehicle?.lastodometerkm || 0,
              orgDistanceUnit.toString() as DISTANCE_UNIT_VALUES
            ) as number) +
            getDistanceByUnitUnrounded(
              dataSingleVehicle?.odometeroffsetkm || 0,
              orgDistanceUnit.toString() as DISTANCE_UNIT_VALUES
            )
          ).toFixed(2)
        ),

        hours_sum: String(
          (
            (dataSingleVehicle?.lastenginehours || 0) +
            (dataSingleVehicle?.enginehoursoffset || 0)
          ).toFixed(2)
        ),
        engine_hours_offset: String(dataSingleVehicle?.enginehoursoffset || 0),
        is_active: dataSingleVehicle?.isactive ? true : false,
        is_border_crossing: dataSingleVehicle?.bordercrossing ? true : false,
        sparseeventreporting: dataSingleVehicle?.sparseeventreporting
          ? true
          : false,
        vehicle_description: dataSingleVehicle?.description || "",
        short_name: dataSingleVehicle?.displayname || "", // TODO: [P] need to check proper keys
        equipment_type: dataSingleVehicle?.vehicleid, // vehicleid is VIN updated automatically by OBD via the GPS device
        vehicle_make: dataSingleVehicle?.vehiclemake || "",
        vehicle_model: dataSingleVehicle?.vehiclemodel || "",
        vehicle_year: dataSingleVehicle?.vehicleyear || null,
        license_plate: dataSingleVehicle?.licenseplate || "",
        license_expiration: !!dataSingleVehicle?.licenseexpire
          ? gregorianDaysToJsDate(dataSingleVehicle?.licenseexpire).getTime()
          : 0,
        vin: dataSingleVehicle?.equipmenttype || "",
        equipment_status: dataSingleVehicle?.equipmentstatus || "",
        asset_type: dataSingleVehicle?.assettype || "",
        vehicle_class: dataSingleVehicle?.vehicleclass || "",
        // imei_number: dataSingleVehicle?.imeinumber,
        // serial_number: dataSingleVehicle?.serialnumber,
        // phone_number: dataSingleVehicle?.simphonenumber,
        // email: dataSingleVehicle?.smsemail || "", // TODO: [P] need to check proper keys
        // group_pushpin_id: dataSingleVehicle?.pushpinid,
        // map_route_color: dataSingleVehicle?.routesegcolorsel || "", // TODO: [P] need to check proper keys
        ignition_input: !!dataSingleVehicle?.ignitionindex
          ? dataSingleVehicle?.ignitionindex
          : parseInt(IGNITION_INPUT_OPTIONS[2].value), // default to IGNITION_ON_OFF
        maximum_speed: !!dataSingleVehicle?.speedlimitkph
          ? parseFloat(
              convertKmphTo(speedUnit, dataSingleVehicle?.speedlimitkph)
                ?.toFixed(2)
                .replace(".00", "")
            )
          : 0,
        driver_id: dataSingleVehicle?.driverid,
        driver_name:
          !!dataSingleVehicle?.driverid && dataSingleDriver
            ? driverDisplayNameForAssetAdmin(dataSingleDriver)
            : "",
        driver_phone_number:
          !!dataSingleVehicle?.driverid && dataSingleDriver
            ? dataSingleDriver?.contactphone
            : "",
        fuel_type: dataSingleVehicle?.fueltype,
        fuel_capacity: Number(
          convertLitersTo(volumeUnit, dataSingleVehicle?.fuelcapacity || 0)
            ?.toFixed(2)
            .replace(".00", "")
        ),
        fuel_economy: Number(
          convertFuelEconomy(
            economyUnit?.toString() as ECONOMY_UNITS,
            dataSingleVehicle?.fueleconomy || 0
          )
            ?.toFixed(2)
            .replace(".00", "")
        ),
        fuel_cost: dataSingleVehicle?.fuelcostperliter || 0,
        recorder_id: dataSingleVehicle?.cameraid || "",
        recorder_on: dataSingleVehicle?.cameraonoff,
        // convert recorder_type to an applicable value
        recorder_type:
          // if saved value is none
          (dataSingleVehicle?.recordertype || CAMERA_TYPES.NONE) ===
          CAMERA_TYPES.NONE
            ? CAMERA_TYPES.NONE
            : // check if saved value is within applicable range
              (enterpriseAccountData?.recordertype === CAMERA_TYPES.SURFSIGHT &&
                  [CAMERA_TYPES.NONE, CAMERA_TYPES.SURFSIGHT].includes(
                    dataSingleVehicle?.recordertype || CAMERA_TYPES.NONE
                  )) ||
                (enterpriseAccountData?.recordertype ===
                  CAMERA_TYPES.VISIONMAX &&
                  [CAMERA_TYPES.NONE, CAMERA_TYPES.VISIONMAX].includes(
                    dataSingleVehicle?.recordertype || CAMERA_TYPES.NONE
                  ))
              ? dataSingleVehicle?.recordertype || CAMERA_TYPES.NONE
              : // if saved value is not within applicable range, set the account default value
                enterpriseAccountData?.recordertype || CAMERA_TYPES.NONE,
        previous_recorder_id: dataSingleVehicle?.previousrecorderid || "",
        recorder_id_last_changed: dbTimeToJsTime(
          dataSingleVehicle?.recorderidlastchange
        ),
        // selected_groups:
        //   dataSingleVehicle?.groups?.map((item) => item.groupid) || [],
        selected_groups:
          dataSingleVehicle?.groups?.map((item) => ({
            label: `${item.groupid}`,
            value: `${item.groupid}`,
          })) || [],
        group_selector: "",
        all_groups:
          !dataSingleVehicle?.groups || dataSingleVehicle?.groups.length === 0,
        lasteventtimestamp: dataSingleVehicle?.lasteventtimestamp,
      } as any);
      uploadFormik.setValues({
        deviceid: dataSingleVehicle.deviceid,
        image: null,
      });
      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [dataSingleVehicle, vehicleId, dataSingleDriver, isFetchingSingleDriver]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;

  // console.log('values', values);
  // console.log("errors", errors);
  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  // handle delete vehicle - THIS HAS BEEN REMOVED AS THE USER SHOULDNT BE ABLE TO DELETE A VEHICLE
  // const handleDeleteVehicle = () => {
  //   deleteSingleVehicleApiTrigger({
  //     account_id: `${thisUserOrganizationId}`,
  //     device_id: vehicleId,
  //   })
  //     .unwrap()
  //     .then(() => {
  //       toast.success(t("toast.vehicle_deleted"));
  //       navigate(routeUrls.dashboardChildren.adminChildren.vehicles);
  //     })
  //     .catch((error) => {
  //       const errors = !!error?.data
  //         ? serializeErrorKeyValues(error?.data)
  //         : [t("toast.deletion_failed")];
  //       toast.error(errors?.join(" "));
  //     });
  // };

  // handle edit vehicle
  const handleEditVehicle = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  const onBackButtonClick = () =>
    navigate(routeUrls.dashboardChildren.adminChildren.vehicles);

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            // { - THIS IS BEING REMOVED AS THE USER SHOULD NOT BE ABLE TO DELETE AN ASSET
            //   customWrapperClass: "w-24",
            //   type: "button",
            //   variant: "danger",
            //   label: tMain("delete"),
            //   onClick: () => {
            //     dispatch(
            //       setModalsData({
            //         ...modalsState,
            //         showDeleteConfirmation: true,
            //       })
            //     );
            //   },
            //   disabled:
            //     isFetchingSingleVehicle ||
            //     // isLoadingDeleteVehicle ||
            //     isLoadingEditVehicleImage ||
            //     getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ASSET_DEVICE) <
            //       ROLE_ACCESS_LEVELS.ALL,
            // },
            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "secondary",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                  } as FormActionButton,
                ]
              : []),
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditVehicle
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingSingleVehicle ||
                isLoadingEditVehicle ||
                isLazyFetchingOrgVehicles ||
                isLoadingEditVehicleImage ||
                dynamicInputLabelsAreSaving ||
                (userCanEdit && !!Object.keys(errors).length) ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ASSET_DEVICE) <
                  ROLE_ACCESS_LEVELS.WRITE,
            },
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleVehicle,
    isLoadingEditVehicle,
    isLazyFetchingOrgVehicles,
    isLoadingEditVehicleImage,
    dynamicInputLabelsAreSaving,
    userCanEdit,
    errors,
    // isLoadingDeleteVehicle,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditVehicle(),
              disabled:
                isLoadingEditVehicle ||
                dynamicInputLabelsAreSaving ||
                !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditVehicle, dynamicInputLabelsAreSaving, errors]);
  const [fieldToReset, setFieldToReset] = useState("");
  const handleConfirmReset = () => {
    if (fieldToReset) {
      formik.setFieldValue(fieldToReset, 0); // Reset the field value
    }
  };
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center">
          <p className="font-semibold text-accent-blue-dark text-lg leading-6">
            {t("sub_heading")}
          </p>
        </div>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${
              isFetchingOrgVehicles ? " opacity-40 pointer-events-none" : ""
            }${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgVehiclesQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {!!results &&
                results.map((item: TLegacyVehicle, index: number) => (
                  <AdminListingColumnItem
                    key={index}
                    selected={vehicleId === item.deviceid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.adminChildren.vehicles}/${getVehicleUUID(item)}`
                      )
                    }
                    title={`${consistentDisplayAssetItem(item.deviceid, item.description, item.displayname, item.drivername, showDriver)}`}
                    // title={`${item?.deviceid ? ` | ${item.deviceid}` : "N/A"}${item?.description ? ` | ${item.description}` : "N/A"}${item?.displayname ? ` | ${item.displayname}` : "N/A"}`}
                    description={`${mapVehicleDisplayTitle(item)}`}
                    asideText={item.equipmenttype}
                    bottomText={""}
                  />
                ))}
            </div>
            {!isFetchingOrgVehicles && (
              <Pagination
                pageSize={orgVehiclesQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      vehicles: {
                        ...orgVehiclesQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count
                    ? Math.ceil(count / orgVehiclesQueryParams.page_size)
                    : 1
                }
                forcePage={orgVehiclesQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      vehicles: {
                        ...orgVehiclesQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${
              isNewEntity?.current
                ? "lg:col-span-12"
                : "col-span-12 xl:col-span-9"
            }`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {(isFetchingSingleVehicle ||
                    isLazyFetchingOrgVehicles ||
                    isLoadingEditVehicleImage) && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditVehicle}
                      disabled={isLoadingEditVehicle || !!Object.keys(errors).length}
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {(isFetchingSingleVehicle ||
                    isLazyFetchingOrgVehicles ||
                    isLoadingEditVehicleImage) && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => { dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true })) }}
                      disabled={isFetchingSingleVehicle || isLoadingDeleteVehicle || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ASSET_DEVICE) < ROLE_ACCESS_LEVELS.ALL}
                    />
                  </div>
                  {userCanEdit && (<div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="secondary"
                      label={tMain("cancel")}
                      onClick={onBackButtonClick}
                    />
                  </div>)}
                  <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={
                        userCanEdit
                          ? handleEditVehicle
                          : () => setUserCanEdit(!userCanEdit)
                      }
                      disabled={isFetchingSingleVehicle || isLoadingEditVehicle || (userCanEdit && !!Object.keys(errors).length) || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ASSET_DEVICE) < ROLE_ACCESS_LEVELS.WRITE}
                    />
                  </div> */}
                  {MemoizedFormActionButtons}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={tMain("sms")}
                      onClick={handleSmsVehicle}
                      disabled={isLoadingEditVehicle}
                    />
                  </div>
                  <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={tMain("command")}
                      onClick={handleCommandVehicle}
                      disabled={isLoadingEditVehicle}
                    />
                  </div> */}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${
                isFetchingSingleVehicle ||
                isLoadingEditVehicle ||
                isLoadingEditVehicleImage ||
                dynamicInputLabelsAreSaving
                  ? "opacity-40"
                  : ""
              }`}
            >
              <Accordian title={t("accord_details")} openByDefault={true}>
                <VehicleDetailForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  formikSetValue={formik.setFieldValue}
                  handleBlur={handleBlur}
                  formik={formik}
                  uploadFormik={uploadFormik}
                  formikSetTouched={formik.setFieldTouched}
                  userCanEdit={userCanEdit}
                  setFieldToReset={setFieldToReset}
                  loadingData={
                    isFetchingSingleVehicle ||
                    isFetchingSingleDriver ||
                    !formikValuesReady ||
                    isLoadingEditVehicleImage
                  }
                  setDecodeVinData={setDecodeVinData}
                />
              </Accordian>
              {thisOrgViewVideoAllowed === "1" &&
                getUserACLAccessLevel(ACL_NAMES.ACL_ASSET_EDIT_CAMERA) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  <Accordian title={t("accord_recorder_info")}>
                    <VehicleCameraIdDetailForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      formikSetValue={formik.setFieldValue}
                      handleBlur={handleBlur}
                      formikSetTouched={formik.setFieldTouched}
                      userCanEdit={
                        getUserACLAccessLevel(
                          ACL_NAMES.ACL_ASSET_EDIT_CAMERA
                        ) >= ROLE_ACCESS_LEVELS.WRITE && userCanEdit
                      }
                      loadingData={
                        isFetchingSingleVehicle || !formikValuesReady
                      }
                    />
                  </Accordian>
                )}
                {thisOrgZipTieAllowed === "1" &&
                getUserACLAccessLevel(ACL_NAMES.ACL_ZIPTIE_CONFIG)>= ROLE_ACCESS_LEVELS.READ &&
                ( 
                <Accordian title={t("accord_zip_tie")} keepMounted={true}>
                <VehicleZipTieForm
                  ref={zipTieFormRef}
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  formikSetValue={formik.setFieldValue}
                  handleBlur={handleBlur}
                  formikSetTouched={formik.setFieldTouched}
                  userCanEdit={userCanEdit}
                  loadingData={isFetchingSingleVehicle || !formikValuesReady}
                  device_id={vehicleId}
                />
              </Accordian>
              )}

              {thisOrgPtoLabelsAllowed === "1" &&
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ASSET_DEVICE) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  // keyed by vehicle so edits never carry over when another
                  // vehicle is picked from the sidebar
                  <VehicleDynamicInputLabels
                    key={vehicleId}
                    ref={dynamicInputLabelsRef}
                    deviceId={`${vehicleId}`}
                    onSavingChange={setDynamicInputLabelsAreSaving}
                    canEdit={
                      userCanEdit &&
                      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ASSET_DEVICE) >=
                        ROLE_ACCESS_LEVELS.WRITE
                    }
                  />
                )}

              <Accordian title={t("accord_group_membership")}>
                <VehicleGroupMembershipForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  formikSetValue={formik.setFieldValue}
                  handleBlur={handleBlur}
                  formikSetTouched={formik.setFieldTouched}
                  userCanEdit={userCanEdit}
                  loadingData={isFetchingSingleVehicle || !formikValuesReady}
                />
              </Accordian>
            </div>
          </div>
        </div>
      </div>
      {/* <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteVehicle} /> */}
      {modalsState?.showModalAssetDecodeVIN && (
        <ModalAssetDecodeVIN
          decodeVinData={decodeVinData}
          dataSingleVehicle={dataSingleVehicle}
          setDecodeVinData={setDecodeVinData}
          formik={formik}
        />
      )}
      <ModalResetConfirmation handleConfirmReset={handleConfirmReset} />
    </>
  );
};

export default ScreenAdminDetailVehicle;
